import { useMemo, useState } from 'react'
import type { ProductoCatalogo, BonoCatalogo } from './catalogo'
import { bonosDelCatalogo, packsDelCatalogo, sueltosDelCatalogo, otrosDelCatalogo, agruparBonosPorLitraje, litrajeBonito } from './catalogo'
import type { FilaTabla } from './tabla'
import { DIAS, bonoDelHabitual } from './tabla'
import { colorDeDia } from './coloresDia'
import type { DiaRuta } from '../../hooks/useFiltrosAgendamiento'
import type { DireccionEntregaElegida } from './filas'
import { SelectorMultiReparto } from './SelectorMultiReparto'
import { Modal } from '../../componentes/Modal'
import { IconoDesplegar, IconoAvisos, IconoConfirmar } from '../../componentes/iconos'
import {
  useGuardarPedidoAcordado,
  useFijarSemana,
  useEnviarAviso,
  type SemanaCiclo,
  type ResultadoAviso,
} from '../../hooks/usePedidoAcordado'

/**
 * Panel lateral de reparto (rediseño B.3 · R9).
 *
 * Al pulsar un cliente en la tabla se abre a la derecha este panel, donde se
 * arma su reparto completo: el bono (seleccionable ±2 del mismo formato), packs
 * y botellones sueltos como extras, y el día de reparto de ese cliente. Antes la
 * tabla solo dejaba «Elegir bono» apretado en la fila; la spec pide una sección
 * de productos que se despliega al pulsar el cliente, con TODOS los productos
 * para los clientes comunes (bonos + packs + sueltos), no solo bonos.
 *
 * El día por cliente (B.3) sustituye a la fecha única de la tanda: cada cliente
 * puede repartirse un día distinto. Si el cliente no tiene ese día en sus rutas,
 * se avisa (la spec lo permite: «ese día no hay ruta en su dirección»).
 */

/** Un extra elegido (pack o suelto) con su cantidad. */
export interface ExtraElegido {
  productoId: number
  nombre: string
  precio: number
  cantidad: number
}

interface Props {
  fila: FilaTabla
  catalogo: ProductoCatalogo[]
  catalogoPendiente: boolean
  /** Bono elegido para este cliente (o el habitual si no se ha tocado). */
  bonoElegido: BonoCatalogo | null
  /** Extras (packs/sueltos) ya elegidos para este cliente. */
  extras: ExtraElegido[]
  /** Día de reparto elegido para este cliente (r1..r5), o null. */
  dia: DiaRuta | null
  onElegirBono: (contactId: string, productoId: string) => void
  onCambiarExtras: (contactId: string, extras: ExtraElegido[]) => void
  onElegirDia: (contactId: string, dia: DiaRuta | null) => void
  /** Direcciones de entrega marcadas para este cliente (multi-reparto). */
  direccionesElegidas?: DireccionEntregaElegida[]
  onCambiarDirecciones?: (dirs: DireccionEntregaElegida[]) => void
  onCerrar: () => void
  /** Revisar y publicar directamente este cliente (atajo desde Configurar). */
  onRevisar?: () => void
  /** La resolución para revisar está en marcha. */
  revisando?: boolean
}

const eur = (n: number) => `${n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`

/** Precio a MOSTRAR: con IVA (como pide la spec), con fallback al base. */
function precioMostrado(p: { precio: number; precio_con_iva?: number }): number {
  return p.precio_con_iva ?? p.precio
}

export function PanelReparto({
  fila,
  catalogo,
  catalogoPendiente,
  bonoElegido,
  extras,
  dia,
  onElegirBono,
  onCambiarExtras,
  onElegirDia,
  direccionesElegidas = [],
  onCambiarDirecciones,
  onCerrar,
  onRevisar,
  revisando,
}: Props) {
  const contactId = fila.cliente.contact_id
  const [buscar, setBuscar] = useState('')

  // Semana del ciclo del cliente (s1..s4): sale de sus etiquetas; el selector la
  // cambia. Pedido acordado + semana se guardan juntos al pulsar "Guardar".
  const semanaInicial = (fila.cliente.semanas ?? []).find((s) => /^s[1-4]$/i.test(s))?.toLowerCase() as
    | SemanaCiclo
    | undefined
  const [semana, setSemana] = useState<SemanaCiclo | null>(semanaInicial ?? null)

  const guardarAcordado = useGuardarPedidoAcordado()
  const fijarSemana = useFijarSemana()
  const enviarAviso = useEnviarAviso()
  const [resultadoAviso, setResultadoAviso] = useState<ResultadoAviso | null>(null)
  const [guardadoOk, setGuardadoOk] = useState(false)

  const todosLosBonos = useMemo(() => bonosDelCatalogo(catalogo), [catalogo])
  const packs = useMemo(() => packsDelCatalogo(catalogo), [catalogo])
  const sueltos = useMemo(() => sueltosDelCatalogo(catalogo), [catalogo])
  const otros = useMemo(() => otrosDelCatalogo(catalogo), [catalogo])

  // El habitual marca el formato: el seleccionable ±2 es del mismo litraje.
  const habitual = useMemo(
    () => bonoElegido ?? bonoDelHabitual(todosLosBonos, fila.pedido_habitual),
    [bonoElegido, todosLosBonos, fila.pedido_habitual],
  )

  // Seleccionable ±2 del mismo formato (R8). Sin habitual, se ofrecen todos los
  // bonos para que el administrador pueda elegir formato manualmente.
  const bonosOfrecibles = useMemo(() => {
    if (!habitual) return todosLosBonos
    const centro = habitual.unidades
    return todosLosBonos.filter(
      (b) => b.litraje === habitual.litraje && b.unidades >= centro - 2 && b.unidades <= centro + 2,
    )
  }, [habitual, todosLosBonos])

  const rutasCliente = new Set(fila.cliente.rutas ?? [])
  const idExtra = (id: number) => extras.find((e) => e.productoId === id)

  const alternarExtra = (p: ProductoCatalogo) => {
    const ya = idExtra(p.id)
    if (ya) {
      onCambiarExtras(contactId, extras.filter((e) => e.productoId !== p.id))
    } else {
      onCambiarExtras(contactId, [
        ...extras,
        { productoId: p.id, nombre: p.nombre, precio: p.precio, cantidad: 1 },
      ])
    }
  }

  const cambiarCantidad = (id: number, delta: number) => {
    onCambiarExtras(
      contactId,
      extras.map((e) =>
        e.productoId === id ? { ...e, cantidad: Math.max(1, e.cantidad + delta) } : e,
      ),
    )
  }

  const filtrar = (lista: ProductoCatalogo[]) => {
    const q = buscar.trim().toLowerCase()
    if (!q) return lista
    return lista.filter((p) => p.nombre.toLowerCase().includes(q))
  }

  // Resumen del pedido de ESTE cliente: el bono elegido + los extras, con el
  // total con IVA (lo que ve el cliente). Es el "carrito" que faltaba: sin él,
  // seleccionabas productos pero no veías qué llevaba ni cuánto sumaba.
  const resumen = useMemo(() => {
    const lineas: { nombre: string; cantidad: number; conIva: number }[] = []
    if (bonoElegido) {
      lineas.push({ nombre: `Bono ${bonoElegido.unidades} (${bonoElegido.litraje}L)`, cantidad: 1, conIva: precioMostrado(bonoElegido) })
    }
    for (const e of extras) {
      const prod = catalogo.find((p) => p.id === e.productoId)
      lineas.push({ nombre: e.nombre, cantidad: e.cantidad, conIva: prod ? precioMostrado(prod) : e.precio })
    }
    const total = lineas.reduce((s, l) => s + l.cantidad * l.conIva, 0)
    return { lineas, total }
  }, [bonoElegido, extras, catalogo])

  // Guarda el pedido acordado (bono + extras) + la semana. NO crea albarán.
  const alGuardarAcordado = async () => {
    setGuardadoOk(false)
    const extrasAcordados = extras.map((e) => ({
      productoId: e.productoId,
      nombre: e.nombre,
      precio: e.precio,
      cantidad: e.cantidad,
    }))
    // Se persiste el bono EFECTIVO que se está mostrando (el elegido, o el del
    // pedido habitual si el equipo no cambió nada). Así "Guardar" no vacía el
    // bono por no haberlo re-clicado: lo que se ve resaltado es lo que se guarda.
    await guardarAcordado.mutateAsync({
      contactId,
      datos: {
        bono: habitual?.unidades ?? null,
        litraje: habitual?.litraje ?? null,
        diaRuta: dia,
        extras: extrasAcordados,
      },
    })
    if (semana) await fijarSemana.mutateAsync({ contactId, semana })
    setGuardadoOk(true)
  }

  // Envía el aviso a ESTE cliente ahora (usa lo que se acaba de guardar).
  const alEnviarAviso = async () => {
    setResultadoAviso(null)
    const r = await enviarAviso.mutateAsync({ contactId })
    setResultadoAviso(r)
  }

  return (
    <Modal titulo={`Reparto de ${fila.cliente.nombre}`} onCerrar={onCerrar} anchoMax="max-w-4xl">
      {/* Cabecera */}
      <div className="flex items-start justify-between gap-3 border-b border-borde px-5 py-4">
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold text-tinta">{fila.cliente.nombre}</h2>
          <p className="mt-0.5 text-xs text-tinta-apagada">
            {fila.cliente.code || 'sin NIF'} · habitual{' '}
            {habitual ? `Bono ${habitual.unidades} (${habitual.litraje}L)` : 'sin bono'}
          </p>
        </div>
        <button
          type="button"
          aria-label="Cerrar el panel"
          onClick={onCerrar}
          className="rounded-md px-2 py-1 text-sm text-tinta-2 ring-1 ring-borde hover:bg-lienzo"
        >
          ✕
        </button>
      </div>

      {/* Cuerpo en 2 columnas: izquierda bono+día, derecha extras. Scrollea dentro. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {catalogoPendiente ? (
          <p className="text-sm text-tinta-apagada">Cargando el catálogo…</p>
        ) : (
          <div className="grid gap-5 md:grid-cols-2">
            {/* Columna izquierda: bono + día de reparto */}
            <div className="flex flex-col gap-5">
              {/* Bono ±2, agrupado por litraje (garrafa + agua) */}
              <Grupo titulo={`Bono${habitual ? ` · ±2 (${litrajeBonito(habitual.litraje)})` : ''}`}>
                <div className="flex flex-col gap-4">
                  {agruparBonosPorLitraje(bonosOfrecibles).map((grupo) => (
                    <div key={grupo.litraje} className="flex flex-col gap-2">
                      <span className="text-[11px] font-semibold text-tinta-apagada">
                        {grupo.titulo} · <span className="text-acento-tinta">{grupo.agua}</span>
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {grupo.bonos.map((bono) => {
                          const sel = habitual?.id === bono.id
                          return (
                            <button
                              key={bono.id}
                              type="button"
                              onClick={() => onElegirBono(contactId, String(bono.id))}
                              className={tarjeta(sel)}
                            >
                              <span className="text-sm font-semibold">Bono {bono.unidades}</span>
                              <span className={sel ? 'text-[11px] text-acento-tinta' : 'text-[11px] text-tinta-apagada'}>
                                {bono.unidades} {bono.unidades === 1 ? 'garrafa' : 'garrafas'}
                              </span>
                              <span className={sel ? 'text-xs font-semibold text-acento-tinta' : 'text-xs font-semibold text-tinta-2'}>
                                {eur(precioMostrado(bono))}
                              </span>
                              {bonoDelHabitual(todosLosBonos, fila.pedido_habitual)?.id === bono.id ? (
                                <span className="text-[10px] font-bold uppercase tracking-wide text-positivo-tinta">
                                  habitual
                                </span>
                              ) : null}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                  {bonoElegido ? (
                    <button
                      type="button"
                      onClick={() => onElegirBono(contactId, '')}
                      className="self-start rounded-lg border border-borde-fuerte px-3 py-2 text-xs font-medium text-tinta-2 transition-colors hover:bg-elevado"
                    >
                      Quitar bono
                    </button>
                  ) : null}
                </div>
              </Grupo>

              {/* Día de reparto por cliente */}
              <Grupo titulo="Día de reparto (este cliente)">
                <div className="flex flex-wrap gap-2">
                  {DIAS.map((d) => {
                    const sel = dia === d.clave
                    const tiene = rutasCliente.has(d.clave)
                    const color = colorDeDia(d.clave)
                    return (
                      <button
                        key={d.clave}
                        type="button"
                        onClick={() => onElegirDia(contactId, sel ? null : d.clave)}
                        className={[
                          'flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-colors',
                          sel ? 'border-acento bg-acento-suave text-tinta' : 'border-borde-fuerte text-tinta-2 hover:bg-elevado',
                        ].join(' ')}
                        title={tiene ? '' : 'Este cliente no tiene ruta ese día'}
                      >
                        <span className={`inline-flex size-4 items-center justify-center rounded-full text-[9px] ${color.clase}`}>
                          {d.clave.slice(1)}
                        </span>
                        {d.corta}
                        {!tiene ? <span className="text-[10px] text-atencion-tinta">sin ruta</span> : null}
                      </button>
                    )
                  })}
                </div>
                {dia && !rutasCliente.has(dia) ? (
                  <p className="mt-2 text-xs text-atencion-tinta">
                    Ese día no hay ruta en la dirección de este cliente.
                  </p>
                ) : null}
              </Grupo>

              {/* Multi-reparto: direcciones de entrega marcadas (un albarán por cada una) */}
              <SelectorMultiReparto
                contactId={contactId}
                elegidas={direccionesElegidas}
                onCambiar={onCambiarDirecciones}
              />

              {/* Semana del ciclo (s1..s4): en qué semana del mes se le reparte.
                  Va junto al día porque ambos definen el ritmo del cliente. */}
              <Grupo titulo="Semana del ciclo (cada cuánto se le reparte)">
                <div className="flex flex-wrap gap-2">
                  {(['s1', 's2', 's3', 's4'] as const).map((s) => {
                    const sel = semana === s
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSemana(sel ? null : s)}
                        className={[
                          'min-w-[52px] rounded-lg border px-3 py-2 text-xs font-semibold uppercase transition-colors',
                          sel ? 'border-positivo-anillo bg-positivo-fondo text-positivo-tinta' : 'border-borde-fuerte text-tinta-2 hover:bg-elevado',
                        ].join(' ')}
                      >
                        {s}
                      </button>
                    )
                  })}
                </div>
              </Grupo>

              {/* Otros / extras: dispensador, fianza, portes, café, estantería,
                  envases. Es lo que antes solo dejaba añadir el "alta suelta";
                  traerlo aquí hace ese modo redundante. Respeta el buscador. */}
              {filtrar(otros).length > 0 ? (
                <GrupoColapsable
                  titulo="Otros / extras"
                  forzarAbierto={buscar.trim() !== '' || filtrar(otros).some((p) => idExtra(p.id))}
                >
                  <ListaExtras
                    productos={filtrar(otros)}
                    idExtra={idExtra}
                    onAlternar={alternarExtra}
                    onCantidad={cambiarCantidad}
                    sufijo=""
                  />
                </GrupoColapsable>
              ) : null}
            </div>

            {/* Columna derecha: buscador + packs + sueltos */}
            <div className="flex flex-col gap-5">
              <input
                type="search"
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
                placeholder="Buscar pack, botellón o extra…"
                className="w-full rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo"
              />

              {filtrar(packs).length > 0 ? (
                <GrupoColapsable
                  titulo="Packs (¿algo más?)"
                  forzarAbierto={buscar.trim() !== '' || filtrar(packs).some((p) => idExtra(p.id))}
                >
                  <ListaExtras
                    productos={filtrar(packs)}
                    idExtra={idExtra}
                    onAlternar={alternarExtra}
                    onCantidad={cambiarCantidad}
                    sufijo="/ud"
                  />
                </GrupoColapsable>
              ) : null}

              {filtrar(sueltos).length > 0 ? (
                <GrupoColapsable
                  titulo="Botellones sueltos"
                  forzarAbierto={buscar.trim() !== '' || filtrar(sueltos).some((p) => idExtra(p.id))}
                >
                  <ListaExtras
                    productos={filtrar(sueltos)}
                    idExtra={idExtra}
                    onAlternar={alternarExtra}
                    onCantidad={cambiarCantidad}
                    sufijo=""
                  />
                </GrupoColapsable>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {/* Pie: resumen del pedido (carrito) + total con IVA + cerrar */}
      <div className="flex flex-col gap-3 border-t border-borde px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          {resumen.lineas.length === 0 ? (
            <p className="text-sm text-tinta-apagada">Aún no has elegido nada para este cliente.</p>
          ) : (
            <div className="flex flex-col gap-0.5">
              <p className="flex flex-wrap items-baseline gap-x-2 text-sm text-tinta-2">
                {resumen.lineas.map((l, i) => (
                  <span key={i} className="whitespace-nowrap">
                    {l.cantidad > 1 ? `${l.cantidad}× ` : ''}{l.nombre}
                    {i < resumen.lineas.length - 1 ? ' ·' : ''}
                  </span>
                ))}
              </p>
              <p className="text-base font-bold text-tinta tabular-nums">
                Total: {eur(resumen.total)} <span className="text-xs font-normal text-tinta-apagada">(IVA incl.)</span>
              </p>
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {/* Acción principal: guardar el pedido acordado (recurrencia). Verde,
              destacada. NO crea albarán. */}
          <button
            type="button"
            onClick={() => void alGuardarAcordado()}
            disabled={guardarAcordado.isPending || fijarSemana.isPending}
            className="flex items-center gap-1.5 rounded-lg bg-positivo-tinta px-5 py-2 text-sm font-semibold text-white transition-[filter] hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <IconoConfirmar className="size-4" />
            {guardarAcordado.isPending || fijarSemana.isPending ? 'Guardando…' : 'Guardar pedido acordado'}
          </button>

          <span className="hidden h-7 w-px bg-borde sm:block" />

          {/* Enviar el aviso a este cliente ahora (usa lo guardado). */}
          <button
            type="button"
            onClick={() => void alEnviarAviso()}
            disabled={enviarAviso.isPending}
            className="flex items-center gap-1.5 rounded-lg border border-borde-fuerte px-4 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado disabled:opacity-60"
          >
            <IconoAvisos className="size-4" />
            {enviarAviso.isPending ? 'Enviando…' : 'Enviar aviso'}
          </button>

          {onRevisar ? (
            <button
              type="button"
              onClick={onRevisar}
              disabled={revisando}
              className="rounded-lg border border-borde-fuerte px-4 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado disabled:cursor-not-allowed disabled:opacity-60"
            >
              {revisando ? 'Preparando…' : 'Revisar y publicar'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onCerrar}
            className="rounded-lg px-4 py-2 text-sm font-medium text-tinta-apagada transition-colors hover:bg-elevado"
          >
            Hecho
          </button>
        </div>
      </div>

      {/* Feedback de guardar / enviar aviso (una línea bajo el pie). */}
      {(guardadoOk || resultadoAviso || guardarAcordado.isError || enviarAviso.isError) ? (
        <div className="border-t border-borde px-5 py-2.5 text-sm">
          {guardarAcordado.isError ? (
            <span className="text-negativo-tinta">No se pudo guardar el pedido acordado.</span>
          ) : guardadoOk && !resultadoAviso ? (
            <span className="text-positivo-tinta">Pedido acordado guardado ✓ — se usará en el próximo aviso de este cliente.</span>
          ) : null}
          {enviarAviso.isError ? (
            <span className="text-negativo-tinta">No se pudo enviar el aviso.</span>
          ) : resultadoAviso ? (
            <span className={resultadoAviso.estado === 'enviado' ? 'text-positivo-tinta' : 'text-aviso-tinta'}>
              {resultadoAviso.mensaje}
            </span>
          ) : null}
        </div>
      ) : null}
    </Modal>
  )
}

function Grupo({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-tinta-apagada">{titulo}</span>
        <span className="h-px flex-1 bg-borde" />
      </div>
      {children}
    </div>
  )
}

/**
 * Grupo plegable: al abrir el panel sale CERRADO (solo el título), para que la
 * columna de packs/sueltos/extras no obligue a hacer scroll si el cliente solo
 * quiere su bono. Se despliega al pulsar la cabecera. `forzarAbierto` lo abre
 * solo (búsqueda activa, o ya hay algo elegido dentro) para no esconder lo que
 * importa.
 */
function GrupoColapsable({
  titulo,
  forzarAbierto = false,
  children,
}: {
  titulo: string
  forzarAbierto?: boolean
  children: React.ReactNode
}) {
  const [abierto, setAbierto] = useState(false)
  const visible = abierto || forzarAbierto
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={visible}
        className="flex items-center gap-2 text-left"
      >
        <span className="text-xs font-semibold uppercase tracking-wide text-tinta-apagada">{titulo}</span>
        <span className="h-px flex-1 bg-borde" />
        <IconoDesplegar
          className={[
            'size-4 shrink-0 text-tinta-apagada transition-transform',
            visible ? 'rotate-180' : '',
          ].join(' ')}
        />
      </button>
      {visible ? children : null}
    </div>
  )
}

function ListaExtras({
  productos,
  idExtra,
  onAlternar,
  onCantidad,
  sufijo,
}: {
  productos: ProductoCatalogo[]
  idExtra: (id: number) => ExtraElegido | undefined
  onAlternar: (p: ProductoCatalogo) => void
  onCantidad: (id: number, delta: number) => void
  sufijo: string
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {productos.map((p) => {
        const elegido = idExtra(p.id)
        return (
          <div key={p.id} className="flex items-center gap-1">
            <button type="button" onClick={() => onAlternar(p)} className={tarjeta(Boolean(elegido))}>
              <span className="text-sm font-semibold">{p.nombre}</span>
              <span className={elegido ? 'text-xs text-acento-tinta' : 'text-xs text-tinta-apagada'}>
                {eur(precioMostrado(p))}
                {sufijo}
              </span>
            </button>
            {elegido ? (
              <span className="inline-flex items-center overflow-hidden rounded-lg border border-borde-fuerte">
                <button
                  type="button"
                  aria-label={`Quitar una unidad de ${p.nombre}`}
                  onClick={() => onCantidad(p.id, -1)}
                  className="bg-elevado px-2 py-1.5 text-sm text-tinta-2 hover:text-tinta"
                >
                  −
                </button>
                <span className="min-w-8 text-center text-sm font-semibold text-tinta">
                  {elegido.cantidad}
                </span>
                <button
                  type="button"
                  aria-label={`Añadir una unidad de ${p.nombre}`}
                  onClick={() => onCantidad(p.id, 1)}
                  className="bg-elevado px-2 py-1.5 text-sm text-tinta-2 hover:text-tinta"
                >
                  +
                </button>
              </span>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

/** Tarjeta seleccionable de producto (bono/pack/suelto). */
function tarjeta(sel: boolean): string {
  return [
    'flex flex-col items-start gap-0.5 rounded-lg border px-3 py-2 text-left transition-colors',
    sel
      ? 'border-acento bg-acento-suave text-tinta'
      : 'border-borde-fuerte text-tinta-2 hover:border-acento hover:bg-elevado',
  ].join(' ')
}
